/**
 * uxBehavior — always-learning UX behavior instrumentation (#589).
 *
 * Captures the raw human-interaction signal the nightly review (Felix)
 * aggregates into navigation funnels, clicks-to-goal, abandonment, and
 * Felix-vs-non-Felix cohort analysis → a suggestion digest to Bob. This is
 * the answer to "learn from how people actually use the app and tell me how
 * to make it better."
 *
 * What it records (all via the allow-listed `ux.behavior.*` telemetry slugs):
 *   - nav         : route A→B with time-on-previous + clicks-on-previous
 *                   (clicks-to-goal is derivable from the nav/click stream)
 *   - click       : actionable clicks (button / link / [role]) — role + slug
 *   - rage_click  : ≥3 rapid clicks on the same target (frustration signal)
 *   - dead_end    : back-navigation with no forward action taken (got lost)
 *   - goal_reached: explicit "reached a key destination" markers
 *
 * PRIVACY (non-negotiable — mirrors the server scrubber):
 *   - `path` is ALWAYS a template (/projects/:id), never a literal id, never
 *     a query string. See `templatePath`.
 *   - `target` is an element ROLE + a short stable slug derived from
 *     data-testid / aria-label / tag — never the element's free text.
 *   - every payload carries `felix_session` (has the user opened Felix this
 *     session?) so the analysis can segment cohorts. No message content, no
 *     names, no emails — ever.
 *
 * Volume: only ACTIONABLE clicks are recorded (not every pixel), fire-and-
 * forget, and the whole module no-ops if telemetry is unavailable. It must
 * never affect UX.
 */
import { recordTelemetry } from "@/lib/telemetry";

const FELIX_SESSION_KEY = "ux_felix_session";

// ---------------------------------------------------------------------------
// Felix cohort signal
// ---------------------------------------------------------------------------

/** Mark that the user engaged Felix this session. Call from Felix open. */
export function markFelixSession(): void {
  try {
    sessionStorage.setItem(FELIX_SESSION_KEY, "1");
  } catch {
    /* sessionStorage unavailable — cohort defaults to false */
  }
}

function felixSession(): boolean {
  try {
    return sessionStorage.getItem(FELIX_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Path templating — strip all PII / high-cardinality segments
// ---------------------------------------------------------------------------

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Convert a concrete pathname into a route template:
 *    /projects/3f2a.../objectives/12  ->  /projects/:id/objectives/:id
 *  Drops the query string entirely. */
export function templatePath(pathname: string): string {
  const clean = (pathname || "/").split("?")[0].split("#")[0];
  return (
    "/" +
    clean
      .split("/")
      .filter(Boolean)
      .map((seg) =>
        UUID_RE.test(seg) || /^\d+$/.test(seg) || seg.length > 40
          ? ":id"
          : seg,
      )
      .join("/")
  );
}

// ---------------------------------------------------------------------------
// Click target → { role, slug } (no free text)
// ---------------------------------------------------------------------------

const ACTIONABLE = "a,button,[role=button],[role=link],[role=tab],[role=menuitem],input[type=submit]";

function slugify(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

function describeTarget(el: Element): { role: string; target: string } | null {
  const actionable = el.closest(ACTIONABLE) as HTMLElement | null;
  if (!actionable) return null;
  const role =
    actionable.getAttribute("role") ||
    actionable.tagName.toLowerCase();
  // Stable slug WITHOUT free text: prefer test-id, then aria-label, then a
  // short class hint. aria-label is operator-authored UI copy (e.g. "Close",
  // "Add contact") — low PII risk and slugified+capped; never element
  // .textContent (which can contain user data).
  const target = slugify(
    actionable.getAttribute("data-testid") ||
      actionable.getAttribute("aria-label") ||
      actionable.getAttribute("name") ||
      actionable.getAttribute("title") ||
      role,
  );
  return { role, target };
}

// ---------------------------------------------------------------------------
// Session state
// ---------------------------------------------------------------------------

let prevPath: string | null = null;
let navStart = 0;
let clicksSinceNav = 0;
let forwardActionTaken = false; // did the user click an actionable since last nav?

// rage detection
let lastTargetKey = "";
let rapidCount = 0;
let lastClickTs = 0;
let installed = false;

function now(): number {
  // performance.now() is monotonic; falls back to Date via wrapper at call.
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Record a navigation transition. Call on every route change (the
 * useUxBehavior hook wires this to react-router's location).
 */
export function noteNavigation(toPathname: string): void {
  const to = templatePath(toPathname);
  const t = now();
  if (prevPath !== null && prevPath !== to) {
    recordTelemetry("ux.behavior.nav", {
      from: prevPath,
      to,
      ms_on_prev: Math.round(t - navStart),
      clicks_on_prev: clicksSinceNav,
      felix_session: felixSession(),
    });
  }
  prevPath = to;
  navStart = t;
  clicksSinceNav = 0;
  forwardActionTaken = false;
}

/** Record reaching a named key destination (clicks/ms since last nav). */
export function noteGoalReached(goal: string): void {
  recordTelemetry("ux.behavior.goal_reached", {
    goal: slugify(goal),
    clicks_since_nav: clicksSinceNav,
    ms_since_nav: Math.round(now() - navStart),
    felix_session: felixSession(),
  });
}

/** Install global click + back-navigation listeners. Idempotent. */
export function initUxBehaviorTracking(): () => void {
  if (installed || typeof document === "undefined") return () => {};
  installed = true;

  const onClick = (e: MouseEvent) => {
    const targetEl = e.target as Element | null;
    if (!targetEl) return;
    const desc = describeTarget(targetEl);
    if (!desc) return; // non-actionable click — ignore (keeps volume sane)

    clicksSinceNav += 1;
    forwardActionTaken = true;
    const path = templatePath(window.location.pathname);

    // Rage detection: ≥3 clicks on the same target within 600ms windows.
    const key = path + "|" + desc.target;
    const t = now();
    if (key === lastTargetKey && t - lastClickTs < 600) {
      rapidCount += 1;
      if (rapidCount === 3) {
        recordTelemetry("ux.behavior.rage_click", {
          path,
          target: desc.target,
          count: rapidCount,
          felix_session: felixSession(),
        });
      }
    } else {
      rapidCount = 1;
    }
    lastTargetKey = key;
    lastClickTs = t;

    recordTelemetry("ux.behavior.click", {
      path,
      role: desc.role,
      target: desc.target,
      felix_session: felixSession(),
    });
  };

  const onPopState = () => {
    // Back/forward nav with no forward action on the page = a likely
    // "got lost / wrong place" dead-end. Cheap, high-signal.
    if (!forwardActionTaken) {
      recordTelemetry("ux.behavior.dead_end", {
        path: templatePath(window.location.pathname),
        reason: "back_no_action",
        felix_session: felixSession(),
      });
    }
  };

  // capture phase so we see the click even if a handler stops propagation
  document.addEventListener("click", onClick, { capture: true });
  window.addEventListener("popstate", onPopState);

  return () => {
    document.removeEventListener("click", onClick, { capture: true });
    window.removeEventListener("popstate", onPopState);
    installed = false;
  };
}
